import { FC, useMemo } from 'react';

import { FreshnessStatus } from '@/__generated__/graphql';
import { ColumnTable, Entry } from '@/components/ColumnTable';
import { NodeStatusIconBadge } from '@/components/NodeStatusIconBadge';
import { RelationName } from '@/components/RelationName';
import { useSource } from '@/hooks/dbtStrategy/useSource';
import { useCommonLineageEntries } from '@/hooks/useCommonLineageEntries';
import { computeSourceFreshness } from '@/util/dbt/freshness';
import { PanelDetails, ResourceDetailsParams } from './panelDetailTypes';
import { TagLinks } from './TagLinks';

const formatThreshold = (count: number, period: string): string => {
  const pluralizedPeriod = count === 1 ? period : `${period}s`;
  return `${count} ${pluralizedPeriod}`;
};

export const useSourceDetailsPanel = ({
  uniqueId,
  projectName,
}: ResourceDetailsParams): PanelDetails => {
  const sourceQuery = useSource(uniqueId);

  const commonEntries = useCommonLineageEntries(projectName);

  return useMemo(() => {
    const source = sourceQuery.data.applied;

    if (!source) {
      return { ...sourceQuery, rows: null, description: null };
    }

    const sourceFreshness = computeSourceFreshness(
      source?.freshness ?? null,
    ) as FreshnessStatus | null;

    const rows: Entry[] = [
      ...commonEntries,
      {
        key: 'Relation',
        data: <RelationName relation={source} />,
        disableTooltip: true,
      },
      {
        key: 'loader',
        data: source.loader,
      },
      {
        key: 'status',
        data: (
          <NodeStatusIconBadge
            status={sourceFreshness ?? 'not available'}
            size="small"
            className="w-full"
          />
        ),
      },
      {
        title: 'Last load',
        key: 'last_load',
        data: source.freshness?.maxLoadedAt ?? 'N/A',
      },
    ];

    // add freshness thresholds rows if configured
    const warnAfter = source.freshness?.criteria?.warnAfter;
    if (warnAfter?.count && warnAfter?.period) {
      rows.push({
        title: 'Warn Threshold',
        key: 'warn_threshold',
        data: formatThreshold(warnAfter.count, warnAfter.period),
      });
    }

    const errorAfter = source.freshness?.criteria?.errorAfter;
    if (errorAfter?.count && errorAfter?.period) {
      rows.push({
        title: 'Error Threshold',
        key: 'error_threshold',
        data: formatThreshold(errorAfter.count, errorAfter.period),
      });
    }

    rows.push(
      {
        key: 'tags',
        data: <TagLinks tags={source.tags} />,
      },
      {
        key: 'Package',
        data: uniqueId.packageName,
      },
    );

    return { ...sourceQuery, rows, description: source.description };
  }, [sourceQuery, commonEntries, uniqueId.packageName]);
};

export const SourceDetailsTable: FC<ResourceDetailsParams> = ({
  className,
  children,
  compact,
  ...props
}) => {
  const sourceDetails = useSourceDetailsPanel(props);
  return (
    <div className={className}>
      {children?.(sourceDetails)}
      <ColumnTable
        tableEntries={sourceDetails.rows}
        compact={compact}
        isLoading={sourceDetails.fetching}
      />
    </div>
  );
};
